import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { useMe } from "~/auth/MeProvider";
import type { WireMe } from "~/auth/me";
import { ScreenPending } from "~/components/ScreenPending";
import { ScreenUnreachable } from "~/components/ScreenUnreachable";
import type { Access } from "./access";
import {
  accountOnlyAccess,
  adminAccess,
  businessAccess,
  consumerOnlyAccess,
  consumerSurfaceAccess,
  guestOnlyAccess,
  managerAccess,
  passwordChangeAccess,
} from "./access";

/**
 * The route guards (R22 T10). They decide nothing; `access.ts` does.
 *
 * **While `/me` is still in flight nothing is decided at all.** Treating "not
 * yet known" as "guest" is the single most likely way to break this rewrite:
 * every cold load would bounce a signed-in member to a log-in screen for a few
 * hundred milliseconds, and any redirect that landed would lose their
 * destination. So the guard renders the app's pending state and waits.
 */

function Gate({
  decide,
  children,
}: {
  decide: (me: WireMe, pathname: string) => Access;
  children: ReactNode;
}) {
  const { me, loading, reload } = useMe();
  const location = useLocation();

  if (loading) return <ScreenPending />;
  // Not knowing who is calling is a different thing from nobody being signed
  // in, and it must not be allowed to look like it: an unreachable API would
  // otherwise bounce every signed-in person to a log-in screen.
  // `!me` rather than `unreachable`: once an answer has arrived, a later
  // failed refresh keeps the answer it already has rather than blanking the
  // screen. Screens that care can read `unreachable` and show a banner.
  if (!me) return <ScreenUnreachable onRetry={() => void reload()} />;

  const verdict = decide(me, location.pathname + location.search);
  if (!verdict.allowed) return <Navigate to={verdict.redirectTo} replace />;

  return <>{children}</>;
}

/** Landing, search, a Business profile, the booking widget (H28). */
export const ConsumerSurface = ({ children }: { children: ReactNode }) => (
  <Gate decide={consumerSurfaceAccess}>{children}</Gate>
);

/** The Consumer's own screens. A guest is sent to log in, carrying the path. */
export const ConsumerOnly = ({ children }: { children: ReactNode }) => (
  <Gate decide={consumerOnlyAccess}>{children}</Gate>
);

/** The business dashboard, including the H44 password gate. */
export const BusinessOnly = ({ children }: { children: ReactNode }) => (
  <Gate decide={businessAccess}>{children}</Gate>
);

/** A clinic's tools: services, hours, team, profile, settings (H26). */
export const ManagerOnly = ({ children }: { children: ReactNode }) => (
  <Gate decide={managerAccess}>{children}</Gate>
);

/** The admin console. */
export const AdminOnly = ({ children }: { children: ReactNode }) => (
  <Gate decide={adminAccess}>{children}</Gate>
);

/** The forced password change, reachable while the flag is still set. */
export const PasswordChangeOnly = ({ children }: { children: ReactNode }) => (
  <Gate decide={passwordChangeAccess}>{children}</Gate>
);

/** The inbox: any signed-in account, because notifications belong to a person. */
export const AccountOnly = ({ children }: { children: ReactNode }) => (
  <Gate decide={accountOnlyAccess}>{children}</Gate>
);

/** Log in, sign up, verify: nothing here for somebody already signed in. */
export const GuestOnly = ({ children }: { children: ReactNode }) => (
  <Gate decide={guestOnlyAccess}>{children}</Gate>
);
